import { Component, ChangeDetectionStrategy, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { Menu, MenuModule } from 'primeng/menu';
import { ToastrService } from 'ngx-toastr';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { EntityState } from '../../core/models';
import { BankAccountDTO, BankAccountService } from '../../services/bank-account.service';

@Component({
  selector: 'app-bank-accounts-list',
  templateUrl: './bank-accounts-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host { display: flex; flex-direction: column; flex: 1 1 0%; min-height: 0; width: 100%; }
      :host ::ng-deep .bank-accounts-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    TagModule,
    MenuModule,
    PERMISSION_UI_DIRECTIVES
  ]
})
export class BankAccountsListComponent {
  private service = inject(BankAccountService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private confirmationService = inject(ConfirmationService);

  @ViewChild('rowMenu') rowMenu!: Menu;

  items = signal<BankAccountDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  pageSizeOptions = [20, 50, 100];
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];
  rowMenuItems: MenuItem[] = [];
  private selected: BankAccountDTO | null = null;
  readonly allFilterValue = 'ALL';
  readonly stateOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  filtersForm = this.fb.group({
    q: [''],
    state: ['ACTIVE' as string]
  });
  hasActiveFilters = signal(false);

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());
    merge(this.filtersForm.controls.state.valueChanges).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.search(this.buildSpec(), { page: this.currentPage(), size: this.pageSize(), sort: ['alias,asc'] }).subscribe({
      next: (response) => {
        if (response.ok) {
          this.items.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          }
        } else {
          this.error.set(response.message || 'Error al cargar cuentas');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('Error al conectar con el servidor.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: { first: number; rows: number }): void {
    const page = event.rows ? Math.floor(event.first / event.rows) : 0;
    const size = event.rows || this.pageSize();
    if (page === this.currentPage() && size === this.pageSize()) return;
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.load();
  }

  applyFilters(): void {
    const { q, state } = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(!!(q?.trim() || (state && state !== EntityState.ACTIVE)));
    this.currentPage.set(0);
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({ q: '', state: EntityState.ACTIVE });
    this.hasActiveFilters.set(false);
    this.currentPage.set(0);
    this.load();
  }

  openRowMenu(event: Event, row: BankAccountDTO): void {
    event.stopPropagation();
    this.selected = row;
    this.rowMenuItems = [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => {
          if (this.selected?.id != null) void this.router.navigate(['/bank-accounts', this.selected.id]);
        }
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        command: () => this.deleteRow(this.selected)
      }
    ];
    this.rowMenu.toggle(event);
  }

  goTo(row: BankAccountDTO): void {
    if (row.id != null) void this.router.navigate(['/bank-accounts', row.id]);
  }

  deleteRow(row: BankAccountDTO | null): void {
    if (!row?.id) return;
    this.confirmationService.confirm({
      header: 'Eliminar cuenta',
      message: `¿Eliminar la cuenta "${row.alias}"?`,
      icon: 'pi pi-trash',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.service.delete(row.id!).subscribe({
          next: (res) => {
            if (res.ok) {
              this.toastr.success('Cuenta eliminada');
              this.load();
            } else {
              this.toastr.error(res.message || 'No se pudo eliminar');
            }
          },
          error: (err) => this.toastr.error(err.error?.message || 'Error al eliminar')
        });
      }
    });
  }

  private buildSpec(): Record<string, unknown> {
    const { q, state } = this.filtersForm.getRawValue();
    const spec: Record<string, unknown> = {};
    if (q?.trim()) spec['q[lk]'] = q.trim();
    if (state && state !== this.allFilterValue) spec['state'] = state;
    return spec;
  }
}
